import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Arrow } from "../components/Layout";

const dimensions = [
  ["Individual truth", "Very strong", "Very strong", "Model-dependent"],
  ["Population coverage", "Low", "Interaction-limited", "Potentially high"],
  ["Repeated observation", "Labour-limited", "Automatic", "Automatic"],
  ["Large samples", "Operationally difficult", "Depends on visits", "Designed for it"],
  ["Drift visibility", "Periodic", "Periodic / continuous", "Continuous potential"],
];

const errorSources = [
  {
    id: "model",
    title: "Individual measurement / model",
    question: "How accurate is one observed bird?",
    text: "This is the error most people notice first. It matters, but it is only one component of the final flock estimate and should not be confused with population representativeness.",
  },
  {
    id: "sampling",
    title: "Sampling",
    question: "How much of the flock is actually represented?",
    text: "Even perfect individual measurements can produce an unstable flock estimate when only a small or systematically selected part of the population is observed.",
  },
  {
    id: "drift",
    title: "Calibration drift",
    question: "Has biology or context shifted since calibration?",
    text: "Genetics, body shape, density and production conditions change over time. A model can remain numerically unchanged while the relationship it learned slowly moves.",
  },
  {
    id: "deployment",
    title: "Deployment environment",
    question: "Did field conditions change the measurement process?",
    text: "Camera geometry, lighting, hardware, data flow and day-to-day operating conditions can all change what the system actually observes in production.",
  },
];

export default function WhyItMattersPage() {
  const [activeError, setActiveError] = useState("sampling");

  return (
    <div className="page page-why">
      <section className="page-hero page-section">
        <div>
          <p className="eyebrow">02 · Why it matters</p>
          <h1>Accurate is not the same as representative.</h1>
        </div>
        <p className="page-hero-lead">
          If a technician measures one bird perfectly, that bird is known perfectly. The flock is not. Birdwatcher starts from this distinction.
        </p>
      </section>

      <section className="page-section truth-section">
        <div className="truth-number">100%</div>
        <div>
          <span className="soft-chip">Perfect individual measurement</span>
          <h2>Assume every sampled bird is measured without error.</h2>
          <p>There is still uncertainty in the flock estimate because the observed birds are only a sample. This separates two problems that are often mixed together: <strong>measurement error</strong> and <strong>sampling error</strong>.</p>
          <Link className="text-link" to="/simulation">Run the sampling simulation <Arrow /></Link>
        </div>
      </section>

      <section className="page-section comparison-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">The three methods</p>
            <h2>Different strengths. Different sampling mechanisms.</h2>
          </div>
          <p>We do not need one method to win every comparison. The useful question is what each method contributes to the final measurement system.</p>
        </div>
        <div className="comparison-table" role="table" aria-label="Measurement method comparison">
          <div className="comparison-row comparison-head" role="row">
            <span>Dimension</span><span>Manual</span><span>Scale</span><span>Camera</span>
          </div>
          {dimensions.map((row, rowIndex) => (
            <motion.div
              className="comparison-row"
              role="row"
              key={row[0]}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.45 }}
              transition={{ delay: rowIndex * 0.04 }}
            >
              {row.map((cell, index) => <span key={cell} className={index === 0 ? "comparison-label" : ""}>{cell}</span>)}
            </motion.div>
          ))}
        </div>
        <p className="comparison-note">No column wins every row. That is the point.</p>
      </section>

      <section className="page-section error-decomposition-section">
        <div className="section-heading error-heading">
          <div>
            <p className="eyebrow">Error decomposition</p>
            <h2>A flock estimate is shaped by more than one source of error.</h2>
          </div>
          <p>A low model error is useful, but it does not tell the whole story. Tap each source to see what it contributes.</p>
        </div>

        <div className="error-accordion" role="list">
          {errorSources.map((item, index) => {
            const open = activeError === item.id;
            return (
              <article className={`error-item error-${item.id} ${open ? "is-open" : ""}`} key={item.id} role="listitem">
                <button
                  type="button"
                  className="error-trigger"
                  aria-expanded={open}
                  onClick={() => setActiveError(open ? null : item.id)}
                >
                  <span className="error-index">0{index + 1}</span>
                  <span className="error-title-wrap">
                    <strong>{item.title}</strong>
                    <small>{item.question}</small>
                  </span>
                  <span className="error-toggle" aria-hidden="true">{open ? "−" : "+"}</span>
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      className="error-detail"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p>{item.text}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </article>
            );
          })}
        </div>

        <div className="error-budget" aria-label="Combined measurement system error">
          <div className="error-budget-copy">
            <span className="soft-chip">The full error budget</span>
            <h3>Model + sampling + drift + deployment</h3>
            <p>What matters operationally is not hiding these terms inside one score, but knowing which one is moving and why.</p>
          </div>
          <div className="error-budget-track" aria-hidden="true">
            <i className="budget-model" />
            <i className="budget-sampling" />
            <i className="budget-drift" />
            <i className="budget-deployment" />
          </div>
        </div>
      </section>

      <section className="page-section quote-section">
        <div className="quote-mark" aria-hidden="true">“</div>
        <blockquote>A system that can explain where it is wrong can be improved.</blockquote>
        <div className="quote-support">
          <p>New genetics are a good example. The biological distribution changes, and model assumptions can change with it.</p>
          <p>That is why we built a rapid calibration workflow instead of treating the initial model as permanent.</p>
        </div>
      </section>

      <section className="page-section next-page-card">
        <span>Next · 03</span>
        <div>
          <h2>Same population. Perfect measurements. Three sample sizes.</h2>
          <p>Draw 50, 300 and 1,000 IID observations and watch the flock estimate move.</p>
        </div>
        <Link className="round-link" to="/simulation" aria-label="Go to simulation">→</Link>
      </section>
    </div>
  );
}
